package dev.terrakok.mapitan.domain

import androidx.compose.ui.graphics.Color
import dev.terrakok.mapitan.domain.utils.generateId
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import org.maplibre.spatialk.geojson.Point

class Project(
    initialDocument: Document = Document(),
) : Editor {
    private var time: Long = 0L

    private val operations: MutableList<Operation> = mutableListOf()

    override val document: StateFlow<Document>
        field = MutableStateFlow(initialDocument)

    private val newOperations: MutableList<Operation> = mutableListOf()
    private var undoneOperations: MutableList<Operation> = mutableListOf()

    override val canUndo: StateFlow<Boolean>
        field = MutableStateFlow(false)

    override val canRedo: StateFlow<Boolean>
        field = MutableStateFlow(false)

    private fun historyUpdate() {
        canUndo.value = newOperations.isNotEmpty()
        canRedo.value = undoneOperations.isNotEmpty()
    }

    private fun applyOperation(operation: Operation) {
        operations += operation
        time++
        historyUpdate()

        document.value = document.value.apply(operation)
    }

    override fun undo() {
        if (newOperations.isEmpty()) return
        val origin = newOperations.removeLast()
        val reverse = origin.reverse(generateId(), time)
        undoneOperations.add(origin)
        applyOperation(reverse)
    }

    override fun redo() {
        if (undoneOperations.isEmpty()) return
        val op = undoneOperations.removeLast()
        newOperations.add(op)
        applyOperation(op)
    }

    private fun applyNewOperation(operation: Operation) {
        newOperations.add(operation)
        undoneOperations.clear()
        applyOperation(operation)
    }

    override fun addPin(pin: Pin) {
        val op = Operation.AddPin(generateId(), time, pin)
        applyNewOperation(op)
    }

    override fun removePin(pin: Pin) {
        val op = Operation.RemovePin(generateId(), time, pin)
        applyNewOperation(op)
    }

    override fun colorPin(pin: Pin, newColor: Color) {
        if (pin.color == newColor) return
        val op = Operation.ColorPin(generateId(), time, pin.id, pin.color, newColor)
        applyNewOperation(op)
    }

    override fun movePin(pin: Pin, newPoint: Point) {
        if (pin.point == newPoint) return
        val op = Operation.MovePin(generateId(), time, pin.id, pin.point, newPoint)
        applyNewOperation(op)
    }

    override fun addLabel(label: Label) {
        val op = Operation.AddLabel(generateId(), time, label)
        applyNewOperation(op)
    }

    override fun removeLabel(label: Label) {
        val op = Operation.RemoveLabel(generateId(), time, label)
        applyNewOperation(op)
    }

    override fun colorLabel(label: Label, newColor: Color) {
        if (label.color == newColor) return
        val op = Operation.ColorLabel(generateId(), time, label.id, label.color, newColor)
        applyNewOperation(op)
    }

    override fun setLabelText(label: Label, newText: String) {
        if (label.text == newText) return
        val op = Operation.NewLabelText(generateId(), time, label.id, label.text, newText)
        applyNewOperation(op)
    }

    override fun moveLabel(label: Label, newPoint: Point) {
        if (label.point == newPoint) return
        val op = Operation.MoveLabel(generateId(), time, label.id, label.point, newPoint)
        applyNewOperation(op)
    }

    override fun addTrack(track: Track) {
        val op = Operation.AddTrack(generateId(), time, track)
        applyNewOperation(op)
    }

    override fun removeTrack(track: Track) {
        val op = Operation.RemoveTrack(generateId(), time, track)
        applyNewOperation(op)
    }

    override fun colorTrack(track: Track, newColor: Color) {
        if (track.color == newColor) return
        val op = Operation.ColorTrack(generateId(), time, track.id, track.color, newColor)
        applyNewOperation(op)
    }

    override fun setTrackLineThickness(track: Track, newThickness: Int) {
        if (track.lineThickness == newThickness) return
        val op = Operation.NewTrackLineThickness(generateId(), time, track.id, track.lineThickness, newThickness)
        applyNewOperation(op)
    }

    override fun setTrackLineStyle(track: Track, newLineStyle: LineStyle) {
        if (track.lineStyle == newLineStyle) return
        val op = Operation.NewTrackLineStyle(generateId(), time, track.id, track.lineStyle, newLineStyle)
        applyNewOperation(op)
    }

    override fun addTrackPoint(track: Track, index: Int, point: TrackPoint) {
        val op = Operation.AddTrackPoint(generateId(), time, track.id, index, point)
        applyNewOperation(op)
    }

    override fun removeTrackPoint(track: Track, point: TrackPoint) {
        val index = track.points.indexOfFirst { it.id == point.id }
        if (index < 0) return
        val op = Operation.RemoveTrackPoint(generateId(), time, track.id, index, point)
        applyNewOperation(op)
    }

    override fun moveTrackPoint(track: Track, point: TrackPoint, newPoint: Point) {
        if (point.point == newPoint) return
        val op = Operation.MoveTrackPoint(generateId(), time, track.id, point.id, point.point, newPoint)
        applyNewOperation(op)
    }

    override fun addArea(area: Area) {
        val op = Operation.AddArea(generateId(), time, area)
        applyNewOperation(op)
    }

    override fun removeArea(area: Area) {
        val op = Operation.RemoveArea(generateId(), time, area)
        applyNewOperation(op)
    }

    override fun colorArea(area: Area, newColor: Color) {
        if (area.color == newColor) return
        val op = Operation.ColorArea(generateId(), time, area.id, area.color, newColor)
        applyNewOperation(op)
    }

    override fun setAreaLineThickness(area: Area, newThickness: Int) {
        if (area.lineThickness == newThickness) return
        val op = Operation.NewAreaLineThickness(generateId(), time, area.id, area.lineThickness, newThickness)
        applyNewOperation(op)
    }

    override fun setAreaLineStyle(area: Area, newLineStyle: LineStyle) {
        if (area.lineStyle == newLineStyle) return
        val op = Operation.NewAreaLineStyle(generateId(), time, area.id, area.lineStyle, newLineStyle)
        applyNewOperation(op)
    }

    override fun addAreaPoint(area: Area, index: Int, point: TrackPoint) {
        val op = Operation.AddAreaPoint(generateId(), time, area.id, index, point)
        applyNewOperation(op)
    }

    override fun removeAreaPoint(area: Area, point: TrackPoint) {
        val index = area.points.indexOfFirst { it.id == point.id }
        if (index < 0) return
        val op = Operation.RemoveAreaPoint(generateId(), time, area.id, index, point)
        applyNewOperation(op)
    }

    override fun moveAreaPoint(area: Area, point: TrackPoint, newPoint: Point) {
        if (point.point == newPoint) return
        val op = Operation.MoveAreaPoint(generateId(), time, area.id, point.id, point.point, newPoint)
        applyNewOperation(op)
    }
}